import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  X,
  RotateCcw,
  ArrowLeft,
  RefreshCw,
  Loader2,
  Clock,
  FileText,
  AlertTriangle,
} from 'lucide-react';
import { Button } from './ui/button';
import { diffWords, getDiffStats } from '../utils/diff';
import { getStoryRevisions, getProfilesByIds } from '../services/simpleStore';
import type { StoryRevision, LanguageCode } from '../types';

// RTL languages (same as WritingTool.tsx)
const RTL_LANGUAGES: LanguageCode[] = ['fa-AF', 'ps-AF'];

interface StoryFields {
  headline: string;
  deck: string;
  body: string;
  slug: string;
  anchorIntro: string;
  script: string;
}

interface VersionHistoryProps {
  storyId: string;
  language: LanguageCode;
  isTvStory: boolean;
  isLocked: boolean;
  currentFields: StoryFields;
  onRestore: (fields: StoryFields) => void;
  onClose: () => void;
}

const PAGE_SIZE = 20;

function extractFieldsFromSnapshot(snapshot: Record<string, unknown>): StoryFields {
  const pm = (snapshot.platform_metadata as Record<string, unknown>) || {};
  return {
    headline: (snapshot.headline as string) || '',
    deck: (snapshot.deck as string) || '',
    body: (snapshot.body as string) || '',
    // TV fields: check platform_metadata first, fallback for pre-migration snapshots
    slug: (pm.slug as string) || (snapshot.slug as string) || '',
    anchorIntro: (pm.anchor_intro as string) || (snapshot.anchor_intro as string) || '',
    script: (pm.script as string) || (snapshot.script as string) || '',
  };
}

function getChangedFields(
  current: Record<string, unknown>,
  previous: Record<string, unknown> | null,
  isTv: boolean,
): string[] {
  if (!previous) return [];
  const curr = extractFieldsFromSnapshot(current);
  const prev = extractFieldsFromSnapshot(previous);
  const changed: string[] = [];

  if (isTv) {
    if (curr.slug !== prev.slug) changed.push('slug');
    if (curr.anchorIntro !== prev.anchorIntro) changed.push('anchor intro');
    if (curr.script !== prev.script) changed.push('script');
  } else {
    if (curr.headline !== prev.headline) changed.push('headline');
    if (curr.deck !== prev.deck) changed.push('deck');
    if (curr.body !== prev.body) changed.push('body');
  }
  return changed;
}

function formatRevisionTime(isoString: string): string {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const VersionHistory: React.FC<VersionHistoryProps> = ({
  storyId,
  language,
  isTvStory,
  isLocked,
  currentFields,
  onRestore,
  onClose,
}) => {
  const [revisions, setRevisions] = useState<StoryRevision[]>([]);
  const [profiles, setProfiles] = useState<Map<string, { fullName: string; email: string }>>(
    new Map(),
  );
  const [loading, setLoading] = useState(true);
  const [selectedRevision, setSelectedRevision] = useState<StoryRevision | null>(null);
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const isRtl = RTL_LANGUAGES.includes(language);

  const loadRevisions = useCallback(
    async (newOffset = 0, append = false) => {
      if (newOffset === 0) setLoading(true);
      else setLoadingMore(true);

      try {
        const revs = await getStoryRevisions(storyId, PAGE_SIZE, newOffset);
        setHasMore(revs.length === PAGE_SIZE);

        const allRevs = append ? [...revisions, ...revs] : revs;
        if (append) {
          setRevisions(allRevs);
        } else {
          setRevisions(revs);
        }
        setOffset(newOffset + revs.length);

        // Batch fetch profiles for new revisions
        const newUserIds = revs
          .map((r) => r.changedBy)
          .filter((id): id is string => !!id);
        if (newUserIds.length > 0) {
          const profileMap = await getProfilesByIds(newUserIds);
          setProfiles((prev) => {
            const merged = new Map(prev);
            profileMap.forEach((v, k) => merged.set(k, v));
            return merged;
          });
        }
      } catch (err) {
        console.error('[VersionHistory] Load error:', err);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [storyId, revisions],
  );

  useEffect(() => {
    loadRevisions(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storyId]);

  const handleRefresh = useCallback(() => {
    setSelectedRevision(null);
    setShowRestoreConfirm(false);
    loadRevisions(0);
  }, [loadRevisions]);

  const handleLoadMore = useCallback(() => {
    loadRevisions(offset, true);
  }, [loadRevisions, offset]);

  // Compute diff for selected revision
  const selectedFields = useMemo(() => {
    if (!selectedRevision) return null;
    return extractFieldsFromSnapshot(selectedRevision.snapshot);
  }, [selectedRevision]);

  const fieldDiffs = useMemo(() => {
    if (!selectedFields) return [];

    const fields = isTvStory
      ? [
          { key: 'slug', label: 'Slug' },
          { key: 'anchorIntro', label: 'Anchor Intro' },
          { key: 'script', label: 'Script' },
        ]
      : [
          { key: 'headline', label: 'Headline' },
          { key: 'deck', label: 'Deck' },
          { key: 'body', label: 'Body' },
        ];

    return fields
      .map((f) => {
        const oldText = selectedFields[f.key as keyof StoryFields];
        const newText = currentFields[f.key as keyof StoryFields];
        if (oldText === newText) return null;
        const diffs = diffWords(oldText, newText);
        const stats = getDiffStats(diffs);
        return { ...f, diffs, stats, oldText, newText };
      })
      .filter(Boolean) as Array<{
      key: string;
      label: string;
      diffs: ReturnType<typeof diffWords>;
      stats: ReturnType<typeof getDiffStats>;
      oldText: string;
      newText: string;
    }>;
  }, [selectedFields, currentFields, isTvStory]);

  const handleRestore = useCallback(() => {
    if (!selectedFields) return;
    onRestore(selectedFields);
    setSelectedRevision(null);
    setShowRestoreConfirm(false);
  }, [selectedFields, onRestore]);

  // --- Render ---

  if (loading) {
    return (
      <div className="h-full flex flex-col bg-white dark:bg-zinc-900 border-l border-zinc-200 dark:border-zinc-800">
        <div className="px-4 py-3 border-b border-zinc-200 dark:border-zinc-800 flex items-center justify-between">
          <h3 className="font-semibold text-zinc-800 dark:text-zinc-200 text-sm flex items-center">
            <Clock className="h-4 w-4 mr-2 text-blue-500" />
            Version History
          </h3>
          <Button variant="ghost" size="icon" onClick={onClose} className="h-7 w-7">
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-white dark:bg-zinc-900 border-l border-zinc-200 dark:border-zinc-800">
      {/* Header */}
      <div className="px-4 py-3 border-b border-zinc-200 dark:border-zinc-800 flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-zinc-800 dark:text-zinc-200 text-sm flex items-center">
            <Clock className="h-4 w-4 mr-2 text-blue-500" />
            Version History
          </h3>
          <span className="text-xs text-zinc-500 dark:text-zinc-400">
            {revisions.length}
            {hasMore ? '+' : ''} version{revisions.length !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleRefresh}
            className="h-7 w-7 text-zinc-400 hover:text-zinc-600"
            title="Refresh"
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
          <Button variant="ghost" size="icon" onClick={onClose} className="h-7 w-7">
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Content */}
      {revisions.length === 0 ? (
        // Empty state
        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
          <FileText className="h-10 w-10 text-zinc-300 dark:text-zinc-600 mb-3" />
          <p className="text-sm text-zinc-500 dark:text-zinc-400">No revision history yet.</p>
          <p className="text-xs text-zinc-400 dark:text-zinc-500 mt-1">
            Revisions are created automatically when you save changes to the content.
          </p>
        </div>
      ) : selectedRevision ? (
        // Diff preview
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {/* Revision info */}
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-zinc-800 dark:text-zinc-200">
                  {selectedRevision.changedBy
                    ? profiles.get(selectedRevision.changedBy)?.fullName || 'Unknown user'
                    : 'Unknown user'}
                </span>
                <span className="text-xs text-zinc-500 dark:text-zinc-400">
                  {formatRevisionTime(selectedRevision.createdAt)}
                </span>
              </div>
              <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">
                {new Date(selectedRevision.createdAt).toLocaleString()}
              </p>
            </div>

            {/* Field diffs */}
            {fieldDiffs.length === 0 ? (
              <div className="text-center py-6">
                <p className="text-sm text-zinc-500 dark:text-zinc-400">
                  No differences from current content.
                </p>
              </div>
            ) : (
              fieldDiffs.map((fd) => (
                <div key={fd.key}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-medium text-zinc-500 dark:text-zinc-400 uppercase">
                      {fd.label}
                    </span>
                    <span className="text-[10px] text-zinc-400">
                      <span className="text-green-600 dark:text-green-400">+{fd.stats.added}</span>
                      {' / '}
                      <span className="text-red-500 dark:text-red-400">-{fd.stats.removed}</span>
                    </span>
                  </div>
                  <div
                    dir={isRtl ? 'rtl' : 'ltr'}
                    className="text-sm leading-relaxed bg-zinc-50 dark:bg-zinc-800/50 rounded-lg p-3 whitespace-pre-wrap border border-zinc-200 dark:border-zinc-700"
                  >
                    {fd.diffs.map((part, i) => (
                      <span
                        key={i}
                        style={{ unicodeBidi: 'plaintext' }}
                        className={
                          part.added
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                            : part.removed
                              ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 line-through'
                              : ''
                        }
                      >
                        {part.value}
                      </span>
                    ))}
                  </div>
                </div>
              ))
            )}

            {/* Restore confirmation */}
            {showRestoreConfirm && (
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                      Replace current content?
                    </p>
                    <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                      Your current unsaved changes will be replaced. You can undo by not saving.
                    </p>
                    <div className="flex gap-2 mt-3">
                      <Button
                        onClick={handleRestore}
                        size="sm"
                        className="bg-amber-600 hover:bg-amber-700 text-white text-xs h-7"
                      >
                        <RotateCcw className="h-3 w-3 mr-1" />
                        Confirm Restore
                      </Button>
                      <Button
                        onClick={() => setShowRestoreConfirm(false)}
                        variant="ghost"
                        size="sm"
                        className="text-xs h-7"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Action bar */}
          <div className="p-3 border-t border-zinc-200 dark:border-zinc-800 flex items-center gap-2">
            <Button
              onClick={() => {
                setSelectedRevision(null);
                setShowRestoreConfirm(false);
              }}
              variant="ghost"
              size="sm"
              className="text-xs"
            >
              <ArrowLeft className="h-3.5 w-3.5 mr-1" />
              Back
            </Button>
            <div className="flex-1" />
            {!showRestoreConfirm && (
              <Button
                onClick={() => setShowRestoreConfirm(true)}
                disabled={isLocked || fieldDiffs.length === 0}
                size="sm"
                className="text-xs bg-blue-600 hover:bg-blue-700 text-white"
                title={isLocked ? 'Story is locked' : fieldDiffs.length === 0 ? 'No differences' : 'Restore this version'}
              >
                <RotateCcw className="h-3.5 w-3.5 mr-1" />
                Restore
              </Button>
            )}
          </div>
        </div>
      ) : (
        // Revision list
        <div className="flex-1 overflow-y-auto">
          <div className="p-3 space-y-1">
            {revisions.map((rev, idx) => {
              const profile = rev.changedBy ? profiles.get(rev.changedBy) : null;
              const prevSnapshot =
                idx < revisions.length - 1 ? revisions[idx + 1].snapshot : null;
              const changedFields = getChangedFields(rev.snapshot, prevSnapshot, isTvStory);

              return (
                <button
                  key={rev.id}
                  onClick={() => setSelectedRevision(rev)}
                  className="w-full text-left p-3 rounded-lg transition-colors hover:bg-zinc-50 dark:hover:bg-zinc-800/50"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-zinc-800 dark:text-zinc-200 truncate">
                      {profile?.fullName || 'Unknown user'}
                    </span>
                    <span className="text-xs text-zinc-500 dark:text-zinc-400 whitespace-nowrap ml-2">
                      {formatRevisionTime(rev.createdAt)}
                    </span>
                  </div>
                  {changedFields.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {changedFields.map((field) => (
                        <span
                          key={field}
                          className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-100 dark:bg-zinc-700 text-zinc-600 dark:text-zinc-300"
                        >
                          {field}
                        </span>
                      ))}
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Load more */}
          {hasMore && (
            <div className="p-3 pt-0">
              <Button
                onClick={handleLoadMore}
                disabled={loadingMore}
                variant="ghost"
                size="sm"
                className="w-full text-xs text-zinc-500"
              >
                {loadingMore ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                ) : null}
                {loadingMore ? 'Loading...' : 'Load older versions'}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VersionHistory;
